import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { LOGO_WHITE_URL } from "@/components/Logo";
import { NewsletterForm } from "@/components/NewsletterForm";
import { PaymentIcons } from "@/components/PaymentIcons";

const QUICK = [
  { label: "Home", to: "/" },
  { label: "New In", to: "/shop/new-in" },
  { label: "Necklace Sets", to: "/shop/necklace-sets" },
  { label: "Bridal Sets", to: "/shop/bridal-sets" },
  { label: "Bangles", to: "/shop/bangles" },
  { label: "Earrings", to: "/shop/earrings" },
  { label: "Sale", to: "/shop/sale" },
  { label: "Contact", to: "/contact" },
];

const CARE = [
  { label: "My Account", to: "/account" },
  { label: "Orders & Returns", to: "/orders-returns" },
  { label: "Shipping Information", to: "/shipping" },
  { label: "Size Guide", to: "/size-guide" },
  { label: "FAQs", to: "/faqs" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms" },
];

const SOCIALS = [
  { name: "Instagram", href: "https://instagram.com/zebaa.uk", d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" },
  { name: "WhatsApp", href: "https://wa.me/447958053932", d: "M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" },
];

const FooterHeading = ({ children }) => (
  <h4 className="text-xs uppercase tracking-[0.22em] font-semibold text-metallic-gold-on-dark w-fit">
    {children}
    <span className="block h-[2px] w-8 mt-2.5 metallic-gold rounded-full" />
  </h4>
);

export const Footer = () => (
  <footer className="relative bg-berry text-white overflow-hidden" data-testid="footer">
    <div className="relative z-10 max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-8">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-x-8 gap-y-12">
        {/* Brand */}
        <div className="col-span-2 md:col-span-4 lg:col-span-4 lg:pr-10">
          <img src={LOGO_WHITE_URL} alt="Zebaa" className="h-9 w-auto object-contain" />
          <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-xs">
            South Asian imitation jewellery on Green Street, London. Bridal sets, kundan-style necklaces, gold-tone bangles and chandbalis - designed to look the part without the fine-jewellery price tag.
          </p>
          <div className="flex items-center gap-3 mt-7">
            {SOCIALS.map((s) => (
              <a key={s.name} href={s.href} target="_blank" rel="noreferrer" aria-label={s.name}
                data-testid={`footer-social-${s.name.toLowerCase()}`}
                className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:border-transparent hover:metallic-gold hover:text-berry transition-all duration-300">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d={s.d} /></svg>
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="lg:col-span-2">
          <FooterHeading>Quick Links</FooterHeading>
          <ul className="mt-5 space-y-2.5 text-sm text-white/70">
            {QUICK.map((q) => (
              <li key={q.label}>
                <Link to={q.to} className="hover:text-gold-bright transition-colors duration-200">{q.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Customer Care */}
        <div className="lg:col-span-3">
          <FooterHeading>Customer Care</FooterHeading>
          <ul className="mt-5 space-y-2.5 text-sm text-white/70">
            {CARE.map((c) => (
              <li key={c.label}>
                <Link to={c.to} className="hover:text-gold-bright transition-colors duration-200">{c.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact + Newsletter */}
        <div className="col-span-2 md:col-span-4 lg:col-span-3">
          <FooterHeading>Contact Us</FooterHeading>
          <ul className="mt-5 space-y-3.5 text-sm text-white/80">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center shrink-0"><Mail className="w-4 h-4 text-gold-bright" /></span>
              <a href="mailto:hello@zebaa.uk" className="hover:text-gold-bright transition-colors break-all">hello@zebaa.uk</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-gold-bright" /></span>
              <span>Unit 1, 312 Green Street,<br />London, E13 9AP</span>
            </li>
          </ul>
          <div className="mt-7">
            <p className="text-xs uppercase tracking-[0.14em] text-white/60 mb-3">Join our newsletter</p>
            <NewsletterForm variant="footer" />
          </div>
        </div>
      </div>

      <div className="mt-14 pt-6 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-white/60">
          © 2026 Zebaa. All rights reserved.
          <Link to="/admin/login" data-testid="footer-admin-link" className="ml-3 text-white/40 hover:text-gold-bright transition-colors">Admin</Link>
        </p>
        <PaymentIcons />
      </div>

      <div className="mt-6 pt-5 border-t border-white/10 text-center">
        <a href="https://oprazero.com/" target="_blank" rel="noreferrer" data-testid="oprazero-credit"
          className="inline-flex items-center gap-2.5 text-xs text-white/50 hover:text-white transition-colors group">
          <span>Designed &amp; Powered by</span>
          <img
            src="/oprazero-logo.png"
            alt="OpraZero"
            className="h-4 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
        </a>
      </div>
    </div>

    <div aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center z-0 overflow-hidden" data-testid="footer-wordmark">
      <span
        className="font-serif tracking-[0.14em] text-white text-[18vw] leading-none whitespace-nowrap opacity-[0.06]"
      >
        Zebaa
      </span>
    </div>
  </footer>
);
